import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { ArrowDownRight, ArrowUpRight } from 'lucide-react'

// Briques communes du theme Muse v2 : formats, titres de section, selecteurs,
// graphiques. Aucun calcul metier ici : on met en forme ce que la base rend.

export const iso = (d: Date) => d.toISOString().slice(0, 10)
export const ilYA = (jours: number) => iso(new Date(Date.now() - jours * 86400e3))

// Espaces ordinaires : l espace fine insecable n existe pas dans toutes les
// polices, et « 1546 $US » se lit mal. On ecrit « 1 546 $ ».
const SYMBOLES: Record<string, string> = { USD: '$', EUR: '€' }
export const chiffres = (v: number, dec = 0) =>
  v.toLocaleString('fr-FR', { minimumFractionDigits: dec, maximumFractionDigits: dec }).replace(/[\u202f\u00a0]/g, ' ')
export const monnaie = (devise: string, dec = 0) => (v: number) => `${chiffres(v, dec)} ${SYMBOLES[devise] ?? devise}`
export const entier = (v: number) => chiffres(Math.round(v))
export const jourCourt = (s: string) => new Date(s + 'T12:00:00Z').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
export const initiales = (nom: string) => nom.split(/\s+/).map((m) => m[0]).join('').slice(0, 2).toUpperCase()

export const STATUTS: Record<string, { l: string; ton: 'ok' | 'attention' | 'alerte' | 'info' }> = {
  performe: { l: 'Performe', ton: 'ok' },
  onboarding_reussi: { l: 'Onboarding réussi', ton: 'ok' },
  onboarding_en_cours: { l: 'Onboarding', ton: 'info' },
  onboarding_a_surveiller: { l: 'À surveiller', ton: 'attention' },
  sous_objectif: { l: 'Sous l’objectif', ton: 'attention' },
  ne_performe_pas: { l: 'Ne performe pas', ton: 'alerte' },
  onboarding_rate: { l: 'Onboarding raté', ton: 'alerte' },
}

export function Libelle({ children }: { children: ReactNode }) { return <span className="ds-libelle">{children}</span> }

export function Section({ libelle, titre, droite, children }: { libelle: string; titre: string; droite?: ReactNode; children: ReactNode }) {
  return (
    <section className="ds-section">
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div><Libelle>{libelle}</Libelle><h2 className="ds-titre-2">{titre}</h2></div>
        {droite}
      </div>
      {children}
    </section>
  )
}

export function Segment<T extends string>({ options, valeur, changer }: { options: { v: T; l: string }[]; valeur: T; changer: (v: T) => void }) {
  return (
    <div className="ds-segment" role="group">
      {options.map((o) => <button key={o.v} aria-pressed={o.v === valeur} onClick={() => changer(o.v)}>{o.l}</button>)}
    </div>
  )
}

function useTaille<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [t, setT] = useState({ l: 0, h: 0 })
  // Mesure immediate avant le premier affichage : sans elle, le graphique
  // attend le ResizeObserver et peut rester vide un instant (constate le 2026-09-10).
  useLayoutEffect(() => {
    if (!ref.current) return
    const r = ref.current.getBoundingClientRect()
    setT({ l: r.width, h: r.height })
  }, [])
  useEffect(() => {
    if (!ref.current) return
    const o = new ResizeObserver(([e]) => setT({ l: e!.contentRect.width, h: e!.contentRect.height }))
    o.observe(ref.current)
    return () => o.disconnect()
  }, [])
  return [ref, t] as const
}

/** Courbe lissee (Catmull-Rom -> Bezier), sans bibliotheque. `plancher` : la
 *  courbe ne descend jamais sous cette ordonnee (pas de cash negatif invente). */
export function chemin(pts: [number, number][], plancher = Infinity) {
  if (pts.length < 2) return ''
  let d = `M${pts[0]![0]},${pts[0]![1]}`
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i]!, p1 = pts[i]!, p2 = pts[i + 1]!, p3 = pts[i + 2] ?? p2
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6]
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6]
    d += ` C${c1[0]},${Math.min(c1[1]!, plancher)} ${c2[0]},${Math.min(c2[1]!, plancher)} ${p2[0]},${p2[1]}`
  }
  return d
}

export function arrondiHaut(v: number) {
  if (v <= 0) return 1
  const p = 10 ** Math.floor(Math.log10(v)), n = v / p
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * p
}

export function Graphique({ serie, format }: { serie: { x: string; y: number }[]; format: (v: number) => string }) {
  const [ref, { l: largeur, h: hauteur }] = useTaille<HTMLDivElement>()
  const [survol, setSurvol] = useState<number | null>(null)
  const h = Math.max(hauteur, 280), g = 46, dr = 8, hautMarge = 12, bas = 28
  const max = arrondiHaut(Math.max(...serie.map((p) => p.y), 0) * 1.1)
  const l = Math.max(largeur - g - dr, 10)
  const px = (i: number) => g + (serie.length <= 1 ? 0 : (i / (serie.length - 1)) * l)
  const py = (v: number) => hautMarge + (1 - v / max) * (h - hautMarge - bas)
  const pts = serie.map((p, i) => [px(i), py(p.y)] as [number, number])
  const ligne = chemin(pts, h - bas)
  const aire = pts.length ? `${ligne} L${px(serie.length - 1)},${h - bas} L${px(0)},${h - bas} Z` : ''
  const graduations = [0, .25, .5, .75, 1].map((f) => max * f)
  const pasX = Math.max(1, Math.ceil(serie.length / 6))
  const actif = survol != null ? serie[survol] : null

  return (
    <div ref={ref} style={{ position: 'relative', flex: 1, minHeight: 280 }}
      onMouseLeave={() => setSurvol(null)}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect()
        const i = Math.round(((e.clientX - r.left - g) / l) * (serie.length - 1))
        setSurvol(Math.min(serie.length - 1, Math.max(0, i)))
      }}>
      {largeur > 0 && (
        <svg className="ds-graphe" width={largeur} height={h} style={{ position: 'absolute', inset: 0 }} role="img" aria-label="Revenu net par jour">
          <defs>
            <linearGradient id="ds-trait" x1="0" x2="1"><stop offset="0" stopColor="#ff5c9e" /><stop offset=".5" stopColor="#b15cff" /><stop offset="1" stopColor="#6d4cff" /></linearGradient>
            <linearGradient id="ds-aire" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor="#b15cff" stopOpacity=".2" /><stop offset="1" stopColor="#ff8cc2" stopOpacity="0" /></linearGradient>
          </defs>
          {graduations.map((v) => (
            <g key={v}>
              <line className="ds-graphe__grille" x1={g} x2={largeur - dr} y1={py(v)} y2={py(v)} />
              <text className="ds-graphe__axe" x={g - 10} y={py(v) + 4} textAnchor="end">{v >= 1000 ? `${chiffres(v / 1000, v % 1000 ? 1 : 0)}k` : Math.round(v)}</text>
            </g>
          ))}
          {serie.map((p, i) => i % pasX === 0 && (
            <text key={p.x} className="ds-graphe__axe" x={px(i)} y={h - 6} textAnchor="middle">{jourCourt(p.x)}</text>
          ))}
          <path d={aire} fill="url(#ds-aire)" />
          <path d={ligne} fill="none" stroke="url(#ds-trait)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          {actif && survol != null && (
            <g>
              <line x1={px(survol)} x2={px(survol)} y1={hautMarge} y2={h - bas} stroke="#e2d9f4" strokeDasharray="3 4" />
              <circle cx={px(survol)} cy={py(actif.y)} r="9" fill="#b15cff" opacity=".15" />
              <circle cx={px(survol)} cy={py(actif.y)} r="4.5" fill="#fff" stroke="#7b5cff" strokeWidth="2.5" />
            </g>
          )}
        </svg>
      )}
      {actif && survol != null && (
        <div className="ds-bulle" style={{ left: px(survol), top: py(actif.y) }}>
          <div className="ds-discret">{new Date(actif.x + 'T12:00:00Z').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
          <div className="ds-nombre" style={{ fontSize: 17, fontWeight: 600 }}>{format(actif.y)}</div>
        </div>
      )}
    </div>
  )
}

export function Etincelle({ valeurs, largeur = 108 }: { valeurs: number[]; largeur?: number }) {
  const w = largeur, h = 34
  if (valeurs.length < 2) return <svg width={w} height={h} />
  const max = Math.max(...valeurs, 1)
  const pts = valeurs.map((v, i) => [(i / (valeurs.length - 1)) * w, h - 3 - (v / max) * (h - 6)] as [number, number])
  return (
    <svg width={w} height={h} aria-hidden>
      <defs><linearGradient id="ds-etincelle" x1="0" x2="1"><stop offset="0" stopColor="#ff5c9e" /><stop offset="1" stopColor="#7b5cff" /></linearGradient></defs>
      <path d={chemin(pts, h - 1)} fill="none" stroke="url(#ds-etincelle)" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

// ------------------------------------------------------------------ dater, toujours

/** « il y a 12 min », « il y a 3 h », « hier », « il y a 4 j ». */
export function ilYa(quand: string | Date | null | undefined) {
  if (!quand) return 'jamais'
  const s = (Date.now() - new Date(quand).getTime()) / 1000
  if (s < 60) return 'à l’instant'
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`
  const j = Math.floor(s / 86400)
  return j === 1 ? 'hier' : `il y a ${j} j`
}

/** Pastille de fraicheur : verte sous 2 h, orange sous 6 h, rouge au-dela. Un chiffre sans date n existe pas.
 *  Une source relevee une fois par jour passe ses propres seuils (26 h, 50 h). */
export function Fraicheur({ source, quand, seuils = [2, 6] }: { source: string; quand: string | null | undefined; seuils?: [number, number] }) {
  const h = quand ? (Date.now() - new Date(quand).getTime()) / 3600e3 : Infinity
  const ton = h < seuils[0] ? 'ok' : h < seuils[1] ? 'attention' : 'alerte'
  return <span className={`ds-badge ds-badge--${ton}`} title={quand ? new Date(quand).toLocaleString('fr-FR') : 'jamais synchronisé'}>{source} · {ilYa(quand)}</span>
}

export const couleursTypes: Record<string, string> = { message: '#8b5cff', tip: '#ff5c9e', ppv: '#ffb3d4', subscription: '#5ad1c2', other: '#c9c2dc' }

function useLargeur<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [l, setL] = useState(0)
  useLayoutEffect(() => { if (ref.current) setL(ref.current.getBoundingClientRect().width) }, [])
  useEffect(() => {
    if (!ref.current) return
    const o = new ResizeObserver(([e]) => setL(e!.contentRect.width))
    o.observe(ref.current)
    return () => o.disconnect()
  }, [])
  return [ref, l] as const
}

/** Colonnes empilees (une par jour ou par mois), avec detail au survol. */
export function Colonnes({ points, parts, libelles, format, hauteur = 260, marque, couleurs = couleursTypes }: {
  points: { x: string; parts: Record<string, number>; etiquette?: string }[]; parts: string[]; libelles: Record<string, string>
  format: (v: number) => string; hauteur?: number; marque?: string; couleurs?: Record<string, string>
}) {
  const [ref, largeur] = useLargeur<HTMLDivElement>()
  const [survol, setSurvol] = useState<number | null>(null)
  const totaux = points.map((p) => parts.reduce((s, k) => s + (p.parts[k] ?? 0), 0))
  const max = arrondiHaut(Math.max(...totaux, 1))
  const g = 44, bas = 26, haut = 10, h = hauteur
  const l = Math.max(largeur - g, 10)
  const pas = points.length ? l / points.length : l
  const bw = Math.max(3, Math.min(34, pas * .62))
  const py = (v: number) => haut + (1 - v / max) * (h - haut - bas)
  const pasX = Math.max(1, Math.ceil(points.length / 7))
  const actif = survol != null ? points[survol] : null
  return (
    <div ref={ref} style={{ position: 'relative' }} onMouseLeave={() => setSurvol(null)}
      onMouseMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); setSurvol(Math.min(points.length - 1, Math.max(0, Math.floor((e.clientX - r.left - g) / pas)))) }}>
      {largeur > 0 && (
        <svg width={largeur} height={h} className="ds-graphe">
          {[0, .5, 1].map((f) => (
            <g key={f}>
              <line className="ds-graphe__grille" x1={g} x2={largeur} y1={py(max * f)} y2={py(max * f)} />
              <text className="ds-graphe__axe" x={g - 8} y={py(max * f) + 4} textAnchor="end">{max * f >= 1000 ? `${chiffres(max * f / 1000, 1)}k` : Math.round(max * f)}</text>
            </g>
          ))}
          {points.map((p, i) => {
            let y = h - bas
            const x = g + i * pas + (pas - bw) / 2
            return (
              <g key={p.x} opacity={survol == null || survol === i ? 1 : .45}>
                {parts.map((k) => {
                  const v = p.parts[k] ?? 0
                  if (!v) return null
                  const hh = (v / max) * (h - haut - bas)
                  y -= hh
                  return <rect key={k} x={x} y={y} width={bw} height={Math.max(hh, 1)} rx={Math.min(6, bw / 2)} fill={couleurs[k] ?? '#b9a8ff'}
                    stroke={p.x === marque ? '#6d4cff' : 'none'} />
                })}
                {i % pasX === 0 && <text className="ds-graphe__axe" x={x + bw / 2} y={h - 6} textAnchor="middle">{p.etiquette ?? jourCourt(p.x)}</text>}
              </g>
            )
          })}
        </svg>
      )}
      {actif && survol != null && (
        <div className="ds-bulle" style={{ left: g + survol * pas + pas / 2, top: py(totaux[survol]!) }}>
          <div className="ds-discret">{actif.etiquette ?? new Date(actif.x + 'T12:00:00Z').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
          <div className="ds-nombre" style={{ fontSize: 17, fontWeight: 600 }}>{format(totaux[survol]!)}</div>
          {parts.filter((k) => actif.parts[k]).map((k) => (
            <div key={k} className="ds-discret" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ width: 8, height: 8, borderRadius: 99, background: couleurs[k] }} />{libelles[k] ?? k} : {format(actif.parts[k]!)}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const PALETTE = ['#7b5cff', '#ff5c9e', '#39b8d8', '#f0a63c', '#27b37d', '#c084fc']

/** Plusieurs courbes (une par lien) + un total en pointilles, legende cliquable. */
export function Lignes({ jours, series, total, hauteur = 300, libelleTotal = 'Tous les liens', uniteTotal = 'subs au total' }: {
  jours: string[]; series: { nom: string; valeurs: number[] }[]; total?: number[]; hauteur?: number; libelleTotal?: string; uniteTotal?: string
}) {
  const [ref, largeur] = useLargeur<HTMLDivElement>()
  const [cache, setCache] = useState<Set<string>>(new Set())
  const [survol, setSurvol] = useState<number | null>(null)
  const visibles = series.filter((s) => !cache.has(s.nom))
  const max = arrondiHaut(Math.max(1, ...(total ?? []), ...visibles.flatMap((s) => s.valeurs)) * 1.1)
  const g = 40, bas = 26, haut = 10, h = hauteur
  const l = Math.max(largeur - g - 8, 10)
  const px = (i: number) => g + (jours.length <= 1 ? 0 : (i / (jours.length - 1)) * l)
  const py = (v: number) => haut + (1 - v / max) * (h - haut - bas)
  const pasX = Math.max(1, Math.ceil(jours.length / 7))
  return (
    <div>
      <div className="ds-legende">
        {series.map((s, i) => (
          <button key={s.nom} className="ds-legende__item" aria-pressed={!cache.has(s.nom)}
            onClick={() => setCache((c) => { const n = new Set(c); n.has(s.nom) ? n.delete(s.nom) : n.add(s.nom); return n })}>
            <span style={{ background: PALETTE[i % PALETTE.length] }} />{s.nom}
          </button>
        ))}
        {total && <span className="ds-legende__item ds-legende__item--total"><span />{libelleTotal}</span>}
      </div>
      <div ref={ref} style={{ position: 'relative' }} onMouseLeave={() => setSurvol(null)}
        onMouseMove={(e) => { const r = e.currentTarget.getBoundingClientRect(); setSurvol(Math.min(jours.length - 1, Math.max(0, Math.round(((e.clientX - r.left - g) / l) * (jours.length - 1))))) }}>
        {largeur > 0 && (
          <svg width={largeur} height={h} className="ds-graphe">
            {[0, .25, .5, .75, 1].map((f) => (
              <g key={f}><line className="ds-graphe__grille" x1={g} x2={largeur - 8} y1={py(max * f)} y2={py(max * f)} />
                <text className="ds-graphe__axe" x={g - 8} y={py(max * f) + 4} textAnchor="end">{Math.round(max * f)}</text></g>
            ))}
            {jours.map((j, i) => i % pasX === 0 && <text key={j} className="ds-graphe__axe" x={px(i)} y={h - 6} textAnchor="middle">{jourCourt(j)}</text>)}
            {total && <path d={chemin(total.map((v, i) => [px(i), py(v)]), h - bas)} fill="none" stroke="#c9bdf0" strokeWidth="2" strokeDasharray="5 5" />}
            {series.map((s, i) => !cache.has(s.nom) && (
              <path key={s.nom} d={chemin(s.valeurs.map((v, k) => [px(k), py(v)]), h - bas)} fill="none" stroke={PALETTE[i % PALETTE.length]} strokeWidth="2.5" strokeLinecap="round" />
            ))}
            {survol != null && <line x1={px(survol)} x2={px(survol)} y1={haut} y2={h - bas} stroke="#e2d9f4" strokeDasharray="3 4" />}
          </svg>
        )}
        {survol != null && (
          <div className="ds-bulle" style={{ left: px(survol), top: haut + 20 }}>
            <div className="ds-discret">{new Date(jours[survol]! + 'T12:00:00Z').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
            {total && <div style={{ fontWeight: 700 }}>{total[survol]} {uniteTotal}</div>}
            {series.map((s, i) => !cache.has(s.nom) && (
              <div key={s.nom} className="ds-discret" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ width: 8, height: 8, borderRadius: 99, background: PALETTE[i % PALETTE.length] }} />{s.nom} : {s.valeurs[survol]}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

/** Mini-histogramme pour une carte (subs par jour d un lien). */
export function MiniBarres({ valeurs, hauteur = 36 }: { valeurs: number[]; hauteur?: number }) {
  const max = Math.max(...valeurs, 1)
  return (
    <div className="ds-minibarres" style={{ height: hauteur }}>
      {valeurs.map((v, i) => <span key={i} style={{ height: `${v ? Math.max(8, (v / max) * 100) : 4}%`, opacity: v ? 1 : .35 }} />)}
    </div>
  )
}

/** Variation contre la periode d avant. Rien avant : « nouveau », pas un +100 % invente. */
export function Delta({ actuel, avant }: { actuel: number; avant: number }) {
  if (!avant) return actuel ? <span className="ds-puce ds-puce--rose">nouveau</span> : null
  const pct = Math.round(((actuel - avant) / avant) * 100)
  return <span className={`ds-variation ds-variation--${pct >= 0 ? 'hausse' : 'baisse'}`}>{pct >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}{pct > 0 ? '+' : ''}{pct} %</span>
}
